<script setup>
/* global axios */
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

// tradução com fallback PT fixo (sobrevive a chunk de locale velho em cache).
const tp = (key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const plans = useMapGetter('reseller/getPlans');

// Marca branca cria planos próprios; revenda por comissão vende NOSSOS planos.
const isWhiteLabel = computed(
  () => currentAccount.value?.partner_type === 'white_label'
);

// Nossos planos padrão (endpoint público) — mostrados read-only p/ revenda.
const standardPlans = ref([]);
const fetchStandardPlans = async () => {
  try {
    const { data } = await axios.get('/public/api/v1/subscription_plans');
    standardPlans.value = data.plans || [];
  } catch (error) {
    standardPlans.value = [];
  }
};

const emptyForm = () => ({
  id: null,
  name: '',
  base_price: 0,
  included_inboxes: 1,
  included_users: 1,
  price_per_inbox: 0,
  price_per_user: 0,
  billing_type: 'pix',
  active: true,
});

const form = ref(emptyForm());
const showForm = ref(false);
const isSaving = ref(false);

const isEditing = computed(() => Boolean(form.value.id));

const billingTypes = [
  { value: 'pix', label: 'PIX' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'credit_card', label: t('REVENDA.PLANS.BILLING.CREDIT_CARD') },
];

const formatBRL = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

// Pré-visualização: total para um cliente de exemplo (2 inboxes, 5 usuários).
const previewInboxes = ref(2);
const previewUsers = ref(5);

const previewTotal = computed(() => {
  const f = form.value;
  const extraInbox = Math.max(0, previewInboxes.value - f.included_inboxes);
  const extraUser = Math.max(0, previewUsers.value - f.included_users);
  return (
    Number(f.base_price) +
    extraInbox * Number(f.price_per_inbox) +
    extraUser * Number(f.price_per_user)
  );
});

const openCreate = () => {
  form.value = emptyForm();
  showForm.value = true;
};

const openEdit = plan => {
  form.value = { ...plan };
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
};

const save = async () => {
  isSaving.value = true;
  try {
    if (isEditing.value) {
      await store.dispatch('reseller/updatePlan', { ...form.value });
    } else {
      await store.dispatch('reseller/createPlan', { ...form.value });
    }
    useAlert(t('REVENDA.PLANS.SAVED'));
    showForm.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.PLANS.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const remove = async plan => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('REVENDA.PLANS.CONFIRM_DELETE', { name: plan.name }))) {
    return;
  }
  try {
    await store.dispatch('reseller/deletePlan', plan.id);
    useAlert(t('REVENDA.PLANS.DELETED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.PLANS.ERROR'));
  }
};

const formatBRLPlain = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

onMounted(() => {
  if (isWhiteLabel.value) {
    store.dispatch('reseller/getPlans');
  } else {
    fetchStandardPlans();
  }
});
</script>

<template>
  <div class="flex flex-col w-full gap-5 py-6">
    <!-- MARCA BRANCA: cria e gerencia planos próprios -->
    <template v-if="isWhiteLabel">
      <header class="flex items-center justify-between">
        <div class="flex flex-col gap-1">
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('REVENDA.PLANS.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('REVENDA.PLANS.SUBTITLE') }}
          </p>
        </div>
        <Button
          v-if="!showForm"
          icon="i-lucide-circle-plus"
          :label="t('REVENDA.PLANS.NEW')"
          @click="openCreate"
        />
      </header>

      <!-- Lista de planos -->
      <div
        v-if="!showForm && plans.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full min-w-full text-sm">
          <thead class="text-left text-n-slate-11 bg-n-solid-2">
            <tr>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.PLANS.NAME') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.PLANS.BASE') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.PLANS.INCLUDED') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ t('REVENDA.PLANS.EXTRA') }}
              </th>
              <th class="px-4 py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="plan in plans" :key="plan.id">
              <td class="px-4 py-3 font-medium text-n-slate-12">
                {{ plan.name }}
                <span v-if="!plan.active" class="ml-1 text-xs text-n-slate-10">
                  {{ t('REVENDA.PLANS.INACTIVE') }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ formatBRL(plan.base_price) }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{
                  t('REVENDA.PLANS.INCLUDED_SUMMARY', {
                    inboxes: plan.included_inboxes,
                    users: plan.included_users,
                  })
                }}
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{
                  t('REVENDA.PLANS.EXTRA_SUMMARY', {
                    inbox: formatBRL(plan.price_per_inbox),
                    user: formatBRL(plan.price_per_user),
                  })
                }}
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap">
                <button
                  type="button"
                  class="text-n-slate-11 hover:text-n-slate-12"
                  @click="openEdit(plan)"
                >
                  <span class="i-lucide-pencil" />
                </button>
                <button
                  type="button"
                  class="ml-3 text-n-ruby-10 hover:text-n-ruby-11"
                  @click="remove(plan)"
                >
                  <span class="i-lucide-trash-2" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-else-if="!showForm"
        class="p-8 text-sm text-center border rounded-xl border-n-weak text-n-slate-11"
      >
        {{ t('REVENDA.PLANS.EMPTY') }}
      </div>

      <!-- Formulário de plano -->
      <div v-if="showForm" class="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div class="flex flex-col gap-4">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('REVENDA.PLANS.NAME') }}
            <input v-model="form.name" type="text" class="w-full" />
          </label>

          <div class="grid grid-cols-2 gap-4">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.BASE_PRICE') }}
              <input
                v-model="form.base_price"
                type="number"
                min="0"
                step="0.01"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.BILLING_TYPE') }}
              <select v-model="form.billing_type">
                <option
                  v-for="bt in billingTypes"
                  :key="bt.value"
                  :value="bt.value"
                >
                  {{ bt.label }}
                </option>
              </select>
            </label>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.INCLUDED_INBOXES') }}
              <input v-model="form.included_inboxes" type="number" min="0" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.INCLUDED_USERS') }}
              <input v-model="form.included_users" type="number" min="0" />
            </label>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.PRICE_PER_INBOX') }}
              <input
                v-model="form.price_per_inbox"
                type="number"
                min="0"
                step="0.01"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('REVENDA.PLANS.PRICE_PER_USER') }}
              <input
                v-model="form.price_per_user"
                type="number"
                min="0"
                step="0.01"
              />
            </label>
          </div>

          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="form.active" type="checkbox" />
            {{ t('REVENDA.PLANS.ACTIVE') }}
          </label>

          <div class="flex gap-2">
            <Button
              :label="t('REVENDA.PLANS.SAVE')"
              :is-loading="isSaving"
              @click="save"
            />
            <Button
              variant="ghost"
              :label="t('REVENDA.PLANS.CANCEL')"
              @click="closeForm"
            />
          </div>
        </div>

        <!-- Simulador -->
        <aside
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2 h-fit"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('REVENDA.PLANS.SIMULATOR') }}
          </p>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('REVENDA.PLANS.SIM_INBOXES') }}
            <input v-model.number="previewInboxes" type="number" min="0" />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('REVENDA.PLANS.SIM_USERS') }}
            <input v-model.number="previewUsers" type="number" min="0" />
          </label>
          <div class="pt-2 mt-1 border-t border-n-weak">
            <span class="text-xs text-n-slate-11">{{
              t('REVENDA.PLANS.SIM_TOTAL')
            }}</span>
            <p class="text-xl font-semibold text-n-slate-12">
              {{ formatBRL(previewTotal) }}
            </p>
          </div>
        </aside>
      </div>
    </template>

    <!-- REVENDA POR COMISSÃO (não marca branca): vê NOSSOS planos, read-only -->
    <template v-else>
      <header class="flex flex-col gap-1">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ tp('REVENDA.PLANS.RESELLER_TITLE', 'Nossos planos') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{
            tp(
              'REVENDA.PLANS.RESELLER_SUBTITLE',
              'Você revende os planos da ChatSuite e ganha comissão. Os planos e preços são definidos por nós — você não cria planos próprios.'
            )
          }}
        </p>
      </header>

      <div
        v-if="standardPlans.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full min-w-full text-sm">
          <thead class="text-left text-n-slate-11 bg-n-solid-2">
            <tr>
              <th class="px-4 py-2 font-medium">
                {{ tp('REVENDA.PLANS.NAME', 'Plano') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ tp('REVENDA.PLANS.MONTHLY', 'Mensal') }}
              </th>
              <th class="px-4 py-2 font-medium">
                {{ tp('REVENDA.PLANS.YEARLY', 'Anual') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="plan in standardPlans" :key="plan.id">
              <td class="px-4 py-3">
                <div class="font-medium text-n-slate-12">{{ plan.name }}</div>
                <div v-if="plan.description" class="text-xs text-n-slate-10">
                  {{ plan.description }}
                </div>
              </td>
              <td class="px-4 py-3 text-n-slate-11 tabular-nums">
                {{ formatBRLPlain(plan.monthly_price) }}
              </td>
              <td class="px-4 py-3 text-n-slate-11 tabular-nums">
                {{ formatBRLPlain(plan.yearly_price) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
